---
title: Code Hosting in Singapore is Now Available
description: "Any developer or organization in Singapore can now host their code right at home on Entire, the first Git forge built for agent-human collaboration."
category: Company
author: Marvin
featureImage: /blog/code-hosting-in-singapore-now-available/hero.jpg
featureImageAlt: "Entirely New Region: Host Code in Singapore, over a painting of the Supertrees rising above a tropical garden."
hideFeatureImage: true
image: /blog/code-hosting-in-singapore-now-available/og.jpg
imageWidth: 1200
imageHeight: 630
---

![Entirely New Region: Host Code in Singapore, over a painting of the Supertrees rising above a tropical garden.](/blog/code-hosting-in-singapore-now-available/hero.jpg)

Today, Entire brings the first Git forge for agent-human collaboration straight to the steamy, beautiful nation of Singapore. Yes, you heard me right: any developer or organization in Singapore can now host their code right at home, on a Git forge built expressly for the scale of agentic coding.

This means that if you are an organization in any Singaporean industry, from financial services to logistics and advanced manufacturing, you now have a source code host that is sovereign, secure, and compliant. And because you pull and clone close to where you work, you and your agents will experience lower latency, and natively hosted repos get significantly faster push times than any other Git host in [production today](https://entire.com/blog/a-git-forge-without-a-center).

By bringing your repo to Entire, you will also gain access to all the capabilities launched just this week, including [Trails](https://entire.com/features/trails), a new way to review and merge AI-generated code that moves beyond the pull request; [Continuous Memory](https://entire.com/features/sessions), which turns a repository into a memory system for agents and developer teams; and the ability to migrate your GitHub Actions pipelines to Buildkite and Depot, so CI keeps up with how fast agents ship. Here’s how to get started:

## Mirroring your GitHub Repo in Singapore

A mirror is a synchronized copy of your repository. The origin remains on GitHub, but the copy lives on Entire in the region of your choice, constantly syncing your changes back to the source.

From [entire.com](https://entire.com), you can designate the Singapore region from the moment you configure GitHub access. Just choose which (or all) of your existing repositories to mirror and then select a region from the dropdown menu.

![The Regions panel on Entire: a repository's GitHub source synced to its primary region, Singapore.](/blog/code-hosting-in-singapore-now-available/mirror-region.jpg)

From the CLI, simply run `entire repo mirror add`. You’ll then be prompted directly in the terminal to choose from our list of available regions to host your mirror. Read more about how mirrors work in our [docs](https://docs.entire.com/).

## Host your Repo Natively on Entire

Of course, if you’d like to have the full-on, Entire native experience, we're full on about that too. If you’re starting from the web or from the terminal, the first step to hosting your repositories on Entire is to create a project. Projects hold your repositories in the region of your choice.

From the web, simply open the Account menu, select Organizations, and choose the organization this project belongs to. Then go to Projects in the left sidebar and select Create project.

Choose your project name, owner, and for the primary region, select Singapore. Then you can create repositories under that project all day.

![The Regions panel on Entire: Singapore as the primary region, replicated to Australia, United States East, the United Kingdom, and India.](/blog/code-hosting-in-singapore-now-available/project-regions.jpg)

From the CLI, the process is similar. Let’s say I wanted to create a project called ‘hawker’ owned by ‘marinabay’ as the organization, and a repo called ‘laksa.’ I’d use the following commands:

1. Create the project in Singapore:

   ```sh
   entire project create hawker --owner marinabay --region sg
   ```

2. Create a repository under the project:

   ```sh
   entire repo create laksa --project hawker
   ```

3. Check the repo’s status to confirm it’s in Singapore:

   ```sh
   entire repo view /et/hawker/laksa
   ```

## Join the Rebellion

Singapore joins our already active regions in the US, EU, UK, India, Australia, with more launching imminently. After all this writing, now I require a Singapore Sling. As always, bring questions, bugs, and multiversal doom to [Discord](https://discord.com/invite/jZJs3Tue4S).
